/**
 * idScanAutofill.js — THE ONE RULE for what a scanned ID may write into a patient's
 * identity, and the ONE test for whether a scanned ID was refused. TEST 1.02 / PROD-176.
 *
 * ★★★ WHY THIS FILE EXISTS, AND WHY IT IS LOAD-BEARING NOW. Before 2026-09-23 the two
 * screens that scan an ID disagreed about what a scan may do to the name already on the
 * account:
 *   · OnboardingIdentity filled BLANKS ONLY — a typed name won.
 *   · ProfileIdentification OVERWROTE — `firstName: extracted.firstName || prev.firstName`.
 * Alex decided on 2026-09-23 that an ID whose name does not match the account is NOT
 * ACCEPTED ("wenn ein Ausweis hochgeladen wird muss der Account-Name mit dem Ausweis-Namen
 * übereinstimmen"). Under that rule the overwrite is not a quirk, it is a bypass: a screen
 * that renames the account to agree with whatever document is held up to it makes a
 * mismatch impossible to observe on the very screen the refusal is written for. The server
 * compares BEFORE the client can write (so the refusal still fires), but a patient who is
 * refused and then SAVES would have the stranger's name written onto their account by the
 * autofill — so the account would be moved to agree with the document it just refused.
 *
 * So there is now exactly ONE rule, here, and both screens call it:
 *   A SCAN NEVER OVERWRITES A FIELD THAT ALREADY HOLDS A VALUE. It fills blanks.
 * The patient can still type over anything themselves; what a scan may not do is change
 * a name on their behalf.
 *
 * ★ ONE EXCEPTION, AND IT IS NOT A NAME. Onboarding's Decision B (v228.11.1) lets the
 *   card's ZIP win over the ZIP typed on the Welcome screen, so street/city/state/ZIP all
 *   come from one source — unless the patient has already edited the ZIP. That survives as
 *   an explicit opt-in (`zipFromCard`), and it can never reach firstName or lastName.
 *
 * ★★ THE REFUSAL TEST LIVES HERE TOO, for the same reason: both screens and the upload
 *   path must answer "was this card refused?" identically. It mirrors the server exactly —
 *   ONLY an explicit `accepted: false` refuses. A missing verdict, a 'not_compared' verdict
 *   (blank surname, failed read) or an insurance card with no verdict at all is NOT refused.
 *   The server is the enforcement (both profile writers drop a refused card whatever the
 *   client sends); this is what lets the app say so at scan time instead of after a save.
 */

const blank = (v) => v === undefined || v === null || String(v).trim() === ''

/** Normalise an OCR'd sex value to the app's vocabulary, or '' when it cannot be read. */
export function normaliseScannedSex(raw) {
  const s = String(raw || '').toLowerCase().trim()
  if (s === 'm' || s.startsWith('mal')) return 'male'
  if (s === 'f' || s.startsWith('fem')) return 'female'
  if (s === 'x' || s.startsWith('oth')) return 'other'
  return ''
}

// The fields a scanned ID may fill. Names FIRST, because they are the reason this exists.
export const ID_SCAN_FIELDS = [
  'firstName', 'lastName', 'dob', 'sex', 'streetAddress', 'city', 'state', 'zipCode',
]

/**
 * The updates a scanned ID may make to `current`. Returns a NEW object holding only the
 * fields to write; the caller merges it. Never overwrites a non-blank value, except the
 * ZIP when `zipFromCard` is set.
 */
export function idScanFill(current, extracted, { zipFromCard = false } = {}) {
  const cur = current || {}
  const ex = extracted || {}
  const out = {}
  for (const f of ID_SCAN_FIELDS) {
    let v = ex[f]
    if (f === 'sex') v = normaliseScannedSex(v)
    if (blank(v)) continue
    if (f === 'zipCode' && zipFromCard) { out[f] = v; continue }
    if (blank(cur[f])) out[f] = v
  }
  return out
}

/** Was this scanned card refused by the server? Only an explicit `accepted: false` is. */
export function isIdScanRefused(cardData) {
  const check = cardData && cardData.idNameCheck
  return !!(check && typeof check === 'object' && check.accepted === false)
}
